import { messageForError } from '../../api/errors'
import { possessive, type User } from '../../api/users'
import { Button } from '../ui/Button'
import { InfoTip } from '../ui/InfoTip'
import { SavedIndicator, SettingRow } from './Setting'
import { useUpdateUser } from './useUpdateUser'

/**
 * Who can see what this child publishes: the platform's private-profile
 * flag, shown as the child's visibility to other kids.
 *
 * The flag hides a child's published games, badges and scores from everyone
 * outside the household; it never stops the child publishing, which is why
 * the row says "hidden" rather than "blocked". Framed as a restriction on
 * purpose: Codeverse is safe by default — other kids never see a full name and
 * there is no messaging, as the "i" beside the label says — so "make private"
 * would invite a reflex flip, whereas hiding is the deliberate exception it
 * should be. One click saves it. Kids know each other by username, so the
 * copy names the child by theirs.
 */
export function PublishedGamesSetting({ child }: { child: User }) {
  const update = useUpdateUser(child)
  const name = child.username
  const visible = !child.privateProfile

  return (
    <SettingRow
      label="Visibility"
      info={
        <InfoTip label="About visibility" testId="visibility-info">
          Codeverse is safe by default: other kids only ever see a username and avatar, never a full
          name, and there is no chat or messaging on Codeverse.
        </InfoTip>
      }
      value={visible ? 'Visible to other kids' : 'Hidden from other kids'}
      helper={
        visible
          ? `Other kids on Codeverse can play ${possessive(name)} published games and see their badges and scores.`
          : `${name} can still create and publish games, but only your family can see and play them.`
      }
      error={update.isError ? messageForError(update.error) : null}
      action={
        <>
          {update.isSuccess && <SavedIndicator />}
          <Button
            variant="secondary"
            onClick={() => update.mutate({ privateProfile: visible })}
            disabled={update.isPending}
            aria-label={
              update.isPending
                ? undefined
                : visible
                  ? `Hide ${possessive(name)} games from other kids`
                  : `Show ${possessive(name)} games to other kids`
            }
            data-testid="setting-toggle"
          >
            {update.isPending ? 'Saving…' : visible ? 'Hide' : 'Show'}
          </Button>
        </>
      }
      testId="setting-published-games"
    />
  )
}
